/**
 * Что открыто на экране — в терминах, понятных бэкенду.
 *
 * Данных задачи или доски здесь нет намеренно: модель добирает их
 * инструментами от имени человека, а всё, что положить сюда, уедет в Gemini
 * независимо от того, спрашивали о нём или нет. Дескриптор отвечает на «где я
 * стою», а не «что я вижу».
 *
 * И он не источник прав: бэкенд проверяет доступ на каждом вызове инструмента
 * токеном человека. Подставленный руками ключ чужого проекта не откроет ничего,
 * чего человеку не открывает трекер.
 */
import type { RouteLocationNormalizedLoaded } from 'vue-router'

import { TrackerAssistantContextType } from '@/api/clients/tracker-assistant'
import { hasAnyFilter, type IssueFilters } from '@/features/issues/filters'
import { i18n } from '@/i18n'

import { emptyContext, type AssistantContext } from './api'

/**
 * Сколько символов выделения уезжает в контекст.
 *
 * Выделяют и абзац, и всю страницу целиком по Ctrl+A. Второе — не вопрос к
 * ассистенту, а промах мышью, и слать его в Gemini не за чем.
 */
export const SELECTION_LIMIT = 600

const paramOf = (route: RouteLocationNormalizedLoaded, name: string): string => {
  const value = route.params[name]

  return Array.isArray(value) ? (value[0] ?? '') : (value ?? '')
}

/** Подпись экрана в чипе: «DEV · Доска», «DEV-1421». */
const labelOf = (routeName: string, key: string): string => {
  const screen = i18n.global.te(`assistant.context.${routeName}`)
    ? i18n.global.t(`assistant.context.${routeName}`)
    : ''

  if (!screen) return key

  return key ? `${key} · ${screen}` : screen
}

/**
 * Контекст открытой страницы.
 *
 * Имя проекта приходит снаружи: в адресе стоит ключ, а в чипе человек ждёт
 * увидеть «Разработка · Доска». Списка проектов у чистой функции нет и быть не
 * должно — она разбирает маршрут, а не ходит в сторы.
 */
export const routeContext = (
  route: RouteLocationNormalizedLoaded,
  projectName = '',
): AssistantContext => {
  const name = String(route.name ?? '')

  if (name === 'issue') {
    const key = paramOf(route, 'key')

    return {
      type: TrackerAssistantContextType.AssistantContextTypeIssue,
      key,
      label: key,
    }
  }

  if (['board', 'issues', 'epics', 'project-settings'].includes(name)) {
    const key = paramOf(route, 'key')

    return {
      type: TrackerAssistantContextType.AssistantContextTypeProject,
      key,
      label: labelOf(name, projectName || key),
    }
  }

  if (name === 'knowledge-page' || name === 'knowledge-page-edit') {
    const pageId = paramOf(route, 'pageId')

    return {
      type: TrackerAssistantContextType.AssistantContextTypePage,
      key: pageId,
      label: labelOf(name, ''),
    }
  }

  // Остальные экраны — разговор ни о чём конкретном: список задач, календарь,
  // настройки. Ключа у них нет, и выдумывать его нечем.
  return { ...emptyContext(), label: labelOf(name, '') }
}

/**
 * Открытая страница целиком: тред, адрес, отбор и выделение.
 *
 * Поля пустых значений не содержат: `{}` вместо отбора экономит не байты, а
 * внимание модели — пустой массив исполнителей она вправе прочесть как «здесь
 * фильтруют по никому».
 */
export interface PageDescriptor {
  /** Тип и ключ треда: по ним разговор находится в истории. */
  context: AssistantContext
  /** Имя маршрута — `board`, `issue`, `my-tasks`. */
  route: string
  projectKey?: string
  projectName?: string
  issueKey?: string
  pageId?: string
  /** Активный отбор доски или списка; отсутствует, если не фильтруют. */
  filters?: IssueFilters
  /** Выделенный на странице текст, обрезанный по SELECTION_LIMIT. */
  selection?: string
}

/** Ни на чём: состояние стора до первого маршрута. */
export const emptyPage = (): PageDescriptor => ({ context: emptyContext(), route: '' })

/** Что к маршруту добавляют экраны и сам человек. */
export interface PageParts {
  projectName?: string
  /** Отбор ровно того экрана, который открыт: у доски и списка он разный. */
  filters?: IssueFilters
  selection?: string
}

/**
 * Дескриптор открытой страницы.
 *
 * Чистая функция: маршрут и то, что к нему добавили снаружи. В сторы она не
 * ходит — за отбором и выделением идёт композабл, а тест проверяет форму
 * дескриптора, не поднимая доску.
 */
export const describePage = (
  route: RouteLocationNormalizedLoaded,
  parts: PageParts = {},
): PageDescriptor => {
  const name = String(route.name ?? '')
  const context = routeContext(route, parts.projectName)

  const page: PageDescriptor = { context, route: name }

  const projectKey = paramOf(route, 'key')
  if (projectKey && name !== 'issue') page.projectKey = projectKey
  if (parts.projectName) page.projectName = parts.projectName

  if (name === 'issue') page.issueKey = paramOf(route, 'key')

  const pageId = paramOf(route, 'pageId')
  if (pageId) page.pageId = pageId

  if (parts.filters && hasAnyFilter(parts.filters)) page.filters = parts.filters

  const selection = (parts.selection ?? '').trim()
  if (selection) page.selection = selection.slice(0, SELECTION_LIMIT)

  return page
}
